import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { MODULES, ACTIONS } from "@/lib/perms";
import { PageHeader, EmptyState } from "@/components/common";
import { TextField, SelectField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const emptyPerms = () => MODULES.reduce((a, m) => ({ ...a, [m.id]: { view: false, edit: false, delete: false } }), {});
const emptyForm = () => ({ id: null, username: "", name: "", pin: "", role: "empresa_user", permissions: emptyPerms() });

export default function CompanyUsers() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const editing = !!form.id;

  const load = () => api.get("/company/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (u) => setForm({ id: u.id, username: u.username, name: u.name, pin: "", role: u.role, permissions: { ...emptyPerms(), ...(u.permissions || {}) } }) || setOpen(true);

  const setPerm = (mid, action, val) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [mid]: { ...f.permissions[mid], [action]: val } } }));
  const toggleModuleAll = (mid, val) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [mid]: { view: val, edit: val, delete: val } } }));

  const save = async () => {
    if (!form.username || !form.name) return toast.error("Usuario y nombre obligatorios");
    if (!editing && form.pin.length !== 4) return toast.error("El PIN debe tener 4 dígitos");
    if (editing && form.pin && form.pin.length !== 4) return toast.error("El PIN debe tener 4 dígitos");
    const body = { username: form.username, name: form.name, role: form.role, permissions: form.permissions };
    if (form.pin) body.pin = form.pin;
    try {
      if (editing) await api.put(`/company/users/${form.id}`, body);
      else await api.post("/company/users", { ...body, pin: form.pin });
      toast.success(editing ? "Usuario actualizado" : "Usuario creado");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (u) => { if (window.confirm(`¿Eliminar a ${u.name}?`)) { try { await api.delete(`/company/users/${u.id}`); load(); } catch (e) { toast.error(errMsg(e)); } } };

  return (
    <div data-testid="usuarios-page">
      <PageHeader title="Usuarios y permisos" subtitle="Administra el acceso de tu equipo por módulo (ver / editar / eliminar)"
        actions={<Button className="rounded-sm gap-2" data-testid="new-user-btn" onClick={openNew}><Plus size={16} /> Nuevo usuario</Button>} />

      {users.length === 0 ? <EmptyState title="Sin usuarios" hint="Crea usuarios con su PIN y define qué módulos pueden usar." /> : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Usuario</TableHead><TableHead>Nombre</TableHead><TableHead>Rol</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                  <TableCell className="font-mono text-xs">{u.username}</TableCell>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-xs">{u.role === "empresa_admin" ? "Administrador" : "Usuario"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="rounded-sm" data-testid={`edit-${u.username}`} onClick={() => openEdit(u)}><PencilSimple size={16} /></Button>
                    <Button variant="ghost" size="icon" className="rounded-sm text-red-600" data-testid={`del-${u.username}`} onClick={() => del(u)}><Trash size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Usuario (corto)" value={form.username} onChange={(v) => setForm((f) => ({ ...f, username: v }))} testId="user-username" />
            <TextField label="Nombre" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="user-name" />
            <TextField label={editing ? "Nuevo PIN (opcional)" : "PIN (4 díg.)"} value={form.pin} onChange={(v) => setForm((f) => ({ ...f, pin: v.replace(/\D/g, "").slice(0, 4) }))} testId="user-pin" />
            <SelectField label="Rol" value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))} testId="user-role"
              options={[{ value: "empresa_user", label: "Usuario (permisos por módulo)" }, { value: "empresa_admin", label: "Administrador (acceso total)" }]} />
          </div>

          {form.role === "empresa_user" && (
            <div className="mt-3 border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Módulo</TableHead>{ACTIONS.map((a) => <TableHead key={a.id} className="text-center">{a.label}</TableHead>)}<TableHead className="text-center">Todo</TableHead></TableRow></TableHeader>
                <TableBody>
                  {MODULES.map((m) => {
                    const p = form.permissions[m.id] || {};
                    const all = m.viewOnly ? p.view : (p.view && p.edit && p.delete);
                    return (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">{m.label}</TableCell>
                        {ACTIONS.map((a) => (
                          <TableCell key={a.id} className="text-center">
                            {m.viewOnly && a.id !== "view"
                              ? <span className="text-muted-foreground">—</span>
                              : <Checkbox checked={!!p[a.id]} data-testid={`perm-${m.id}-${a.id}`} onCheckedChange={(v) => setPerm(m.id, a.id, !!v)} />}
                          </TableCell>
                        ))}
                        <TableCell className="text-center">
                          {m.viewOnly
                            ? <Checkbox checked={!!p.view} data-testid={`perm-${m.id}-all`} onCheckedChange={(v) => setPerm(m.id, "view", !!v)} />
                            : <Checkbox checked={all} data-testid={`perm-${m.id}-all`} onCheckedChange={(v) => toggleModuleAll(m.id, !!v)} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-user-btn" onClick={save}>{editing ? "Guardar" : "Crear"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
